import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal } from 'react-native';
import { CompletedTask } from '../store/useActivityStore';
import { SkiaCanvas } from './SkiaCanvas';
import { SvgTemplateOverlay } from './SvgTemplateOverlay';
import { useNotificationStore } from '../store/NotificationStore';
import { InputStroke } from '@eduwrite/shared';

interface TaskDetailModalProps {
    task: CompletedTask;
    onClose: () => void;
}

export const TaskDetailModal: React.FC<TaskDetailModalProps> = ({ task, onClose }) => {
    const config = task.activity.config as unknown;
    const targetText = (config as any)?.targetText;
    const svgUrl = (config as any)?.svgTemplateUrl;

    // Determine title
    let displayTitle = targetText || task.activity.title;
    if (task.activity.type === 'maths') displayTitle = (config as any).expression || 'Math';
    if (task.activity.type === 'spelling' || task.activity.type === 'common_exception_words') displayTitle = (config as any).word || 'Word Activity';

    const mechanic = task.activity.mechanic || 'canvas';
    const usesCanvas = mechanic === 'canvas';
    const scoreLabel = usesCanvas ? 'Neatness Score:' : 'Score:';

    // Find if there is any feedback for this specific task
    const { notifications, markAsRead } = useNotificationStore();
    const feedbackNotification = targetText ? [...notifications]
        .reverse()
        .find(n => n.payload?.targetText === targetText) : undefined;

    React.useEffect(() => {
        if (feedbackNotification && !feedbackNotification.read) {
            markAsRead(feedbackNotification.id);
        }
    }, [feedbackNotification, markAsRead]);

    const overlayStrokes = feedbackNotification?.payload?.overlay as InputStroke[] | undefined;

    return (
        <Modal
            visible={true}
            animationType="slide"
            presentationStyle="pageSheet"
            onRequestClose={onClose}
        >
            <View style={styles.container} accessibilityLabel="Submission details">
                <View style={styles.header}>
                    <View>
                        <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Submission Details</Text>
                        {displayTitle && <Text style={styles.subtitle} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Activity: {displayTitle}</Text>}
                    </View>
                    <TouchableOpacity onPress={onClose} style={styles.closeButton} accessibilityRole="button" accessibilityLabel="Close" accessibilityHint="Double tap to close the submission details">
                        <Text style={styles.closeText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Close</Text>
                    </TouchableOpacity>
                </View>

                <View style={styles.contentArea}>
                    {(usesCanvas || mechanic === 'word_tap' || mechanic === 'numpad') && (
                        <View style={styles.canvasContainer}>
                            <SkiaCanvas
                                readOnly={true}
                                strokes={task.strokes}
                                overlay={overlayStrokes}
                            />
                            {usesCanvas && <SvgTemplateOverlay svgTemplateUrl={svgUrl} targetText={targetText} />}
                        </View>
                    )}

                    {/* Show non-canvas answers if they exist */}
                    {task.structuredData ? (
                        <View style={styles.answerSection}>
                            {task.activity.type === 'maths' && (
                                <View style={styles.mathContainer}>
                                    <Text style={styles.answerLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Submitted Answer:</Text>
                                    <Text style={styles.answerText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{(task.structuredData as any).answer}</Text>
                                </View>
                            )}

                            {task.activity.mechanic === 'word_tap' && (
                                <View style={styles.spellingContainer}>
                                    <Text style={styles.answerLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Selected Word:</Text>
                                    <Text style={styles.spellingText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{(task.structuredData as any).tapped}</Text>
                                    {(task.structuredData as any).correct ?
                                        <Text style={styles.correctLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✓ Correct</Text> :
                                        <Text style={styles.wrongLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✗ Expected: {(task.structuredData as any).expected}</Text>
                                    }
                                </View>
                            )}

                            {task.activity.type === 'sentence_building' && (
                                <View style={styles.spellingContainer}>
                                    <Text style={styles.answerLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Built Sentence:</Text>
                                    <Text style={styles.sentenceText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{(task.structuredData as any).builtSentence}</Text>
                                </View>
                            )}
                        </View>
                    ) : null}

                    {(usesCanvas && (!task.strokes || task.strokes.length === 0)) && (
                        <View style={styles.warningContainer}>
                            <Text style={styles.warningText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>No work found for this submission.</Text>
                        </View>
                    )}
                </View>

                <View style={styles.footer}>
                    {feedbackNotification && (
                        <View style={styles.feedbackCard}>
                            <Text style={styles.feedbackLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Teacher Feedback:</Text>
                            <Text style={styles.feedbackText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{feedbackNotification.message}</Text>
                        </View>
                    )}
                    {task.score !== undefined ? (
                        <View style={[styles.scoreCard, feedbackNotification ? { marginTop: 12 } : {}]}>
                            <Text style={styles.scoreLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{scoreLabel}</Text>
                            <Text style={styles.scoreText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{task.score}%</Text>
                        </View>
                    ) : (
                        <View style={[styles.scoreCard, feedbackNotification ? { marginTop: 12 } : {}]}>
                            <Text style={styles.scoreText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>No score available</Text>
                        </View>
                    )}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F8FAFC',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#1E293B',
    },
    subtitle: {
        fontSize: 14,
        color: '#64748B',
        marginTop: 2,
    },
    closeButton: {
        backgroundColor: '#64748B',
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderRadius: 20,
    },
    closeText: {
        color: '#FFFFFF',
        fontWeight: '600',
    },
    contentArea: {
        flex: 1,
        margin: 16,
        gap: 16,
    },
    canvasContainer: {
        flex: 1,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        overflow: 'hidden',
        position: 'relative',
    },
    answerArea: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        alignItems: 'center',
    },
    answerSection: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        alignItems: 'center',
    },
    mathContainer: { alignItems: 'center', gap: 12 },
    answerLabel: { fontSize: 14, color: '#64748B', fontWeight: 'bold' },
    mathAnswer: { alignItems: 'center', gap: 12 },
    expressionText: { fontSize: 32, fontWeight: 'bold', color: '#1E293B' },
    answerBox: { paddingHorizontal: 24, paddingVertical: 12, backgroundColor: '#EFF6FF', borderRadius: 12, borderWidth: 2, borderColor: '#3B82F6' },
    answerText: { fontSize: 32, fontWeight: 'bold', color: '#3B82F6' },
    spellingContainer: { alignItems: 'center', gap: 8 },
    spellingLabel: { fontSize: 14, color: '#64748B', fontWeight: 'bold' },
    spellingText: { fontSize: 32, fontWeight: 'bold', color: '#1E293B', letterSpacing: 2 },
    correctLabel: { color: '#10B981', fontWeight: 'bold', fontSize: 16 },
    wrongLabel: { color: '#EF4444', fontWeight: 'bold', fontSize: 16 },
    sentenceAnswer: { width: '100%', gap: 8 },
    sentenceLabel: { fontSize: 14, color: '#64748B', fontWeight: 'bold' },
    sentenceText: { fontSize: 20, color: '#1E293B', lineHeight: 28, fontWeight: '600' },
    warningContainer: {
        position: 'absolute',
        top: 10,
        left: 0,
        right: 0,
        alignItems: 'center',
    },
    warningText: {
        backgroundColor: 'rgba(255, 200, 0, 0.8)',
        padding: 8,
        borderRadius: 8,
        fontSize: 12,
        color: '#000',
    },
    footer: {
        padding: 20,
        backgroundColor: '#FFFFFF',
        borderTopWidth: 1,
        borderTopColor: '#E2E8F0',
    },
    scoreCard: {
        backgroundColor: '#F0FDF4',
        padding: 16,
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#10B981',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    scoreLabel: {
        fontSize: 16,
        fontWeight: '700',
        color: '#1E293B',
    },
    scoreText: {
        fontSize: 24,
        fontWeight: 'bold',
        color: '#10B981',
    },
    feedbackCard: {
        backgroundColor: '#F0F9FF',
        padding: 16,
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#3B82F6',
    },
    feedbackLabel: {
        fontSize: 12,
        fontWeight: '700',
        color: '#3B82F6',
        marginBottom: 4,
        textTransform: 'uppercase',
    },
    feedbackText: {
        fontSize: 16,
        color: '#1E293B',
        lineHeight: 24,
    },
});
